import isEmpty from "lodash-es/isEmpty"
import keys from "lodash-es/keys"
import map from "lodash-es/map"
import values from "lodash-es/values"

import { isJwtTokenLive, isJwtTokenValid } from "$/utils/tokens"

import { minutes } from "@/3514/utils"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"

import {
    ECoachingChatMessageAuthor,
    type ICoachingChatConversationQueryDto,
    type ICoachingChatConversationQueryModel,
    type ICoachingChatMessageQueryDto,
    type ICoachingChatMessageQueryModel,
    type ICoachingChatNewMessagesErrorQueryDto as IErrorDto,
    type ICoachingChatNewMessagesErrorQueryModel as IErrorModel,
    type ICoachingChatMessageQueryDto as IMessageDto,
    type ICoachingChatMessageQueryModel as IMessageModel,
    type ICoachingChatNewMessagesQueryDto as INewMessagesDto,
    type ICoachingChatNewMessagesQueryModel as INewMessagesModel,
    type ICoachingChatConversationSummaryCardQueryDto as ISummaryCardDto,
    type ICoachingChatConversationSummaryCardQueryModel as ISummaryCardModel,
    type TCoachingChatMessageAuthor
} from "../types"

const storageService: StorageService = new StorageService(EStorageServiceType.Session)

const socket = {
    setToken(token: string): void {
        storageService.setItem(SessionStorageServiceKeys.ChatSocketToken(), token)
    },
    isTokenValid(): boolean {
        const _token: string = storageService.getItem(SessionStorageServiceKeys.ChatSocketToken()) ?? String("")

        return isJwtTokenValid(_token) && isJwtTokenLive(_token, minutes(1))
    }
}

function getChatMessageAuthorEnumFromType(author: TCoachingChatMessageAuthor): ECoachingChatMessageAuthor {
    switch (author) {
        case "assistant":
            return ECoachingChatMessageAuthor.AI
        case "user":
            return ECoachingChatMessageAuthor.User
        default:
            return null
    }
}

function getSummaryCardModelsFromDtos(dtos: ISummaryCardDto[], date?: string): ISummaryCardModel[] {
    return isEmpty(dtos)
        ? []
        : map<ISummaryCardDto, ISummaryCardModel>(
              dtos,
              (card: ISummaryCardDto): ISummaryCardModel => ({
                  title: keys(card)[0],
                  content: values(card)[0],
                  ...(!!date && { completedAt: date })
              })
          )
}

function getChatMessageModelFromDto(dto: ICoachingChatMessageQueryDto): ICoachingChatMessageQueryModel {
    return isEmpty(dto)
        ? null
        : {
              id: dto.id,
              conversationId: dto.conversation_id,
              conversation: getChatConversationModelFromDto(dto.conversation),
              message: dto.message,
              author: getChatMessageAuthorEnumFromType(dto.author_role)
          }
}

function getChatConversationModelFromDto(
    dto: Partial<ICoachingChatConversationQueryDto>
): ICoachingChatConversationQueryModel {
    return isEmpty(dto)
        ? {
              completedStamp: null,
              id: null,
              summaryCards: [],
              status: null,
              isMatrixFeedbackCompleted: false,
              messages: []
          }
        : {
              completedStamp: dto.completed_stamp,
              id: dto.id,
              summaryCards: getSummaryCardModelsFromDtos(dto.summary_cards),
              status: dto.status,
              isMatrixFeedbackCompleted: dto.is_matrix_feedback_completed,
              messages: isEmpty(dto.messages)
                  ? []
                  : map<ICoachingChatMessageQueryDto, ICoachingChatMessageQueryModel>(
                        dto.messages,
                        (msg: ICoachingChatMessageQueryDto): ICoachingChatMessageQueryModel =>
                            getChatMessageModelFromDto(msg)
                    )
          }
}

function getChatNewMessageModelFromDto(dto: INewMessagesDto): INewMessagesModel {
    return isEmpty(dto)
        ? null
        : {
              errors: isEmpty(dto.errors)
                  ? []
                  : map<IErrorDto, IErrorModel>(
                        dto.errors,
                        ({
                            message_id: messageId,
                            moderation_id: moderationId,
                            conversation_id: conversationId,
                            msg: message
                        }: IErrorDto): IErrorModel => ({
                            messageId,
                            moderationId,
                            conversationId,
                            message
                        })
                    ),
              messages: isEmpty(dto.messages)
                  ? []
                  : map<IMessageDto, IMessageModel>(
                        dto.messages,
                        (msg: IMessageDto): IMessageModel => getChatMessageModelFromDto(msg)
                    )
          }
}

const utils = {
    socket,
    getSummaryCardModelsFromDtos,
    getChatMessageModelFromDto,
    getChatConversationModelFromDto,
    getChatNewMessageModelFromDto,
    getChatMessageAuthorEnumFromType
}

export { utils as coachingChatApiUtils }
